import { memo, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import InstrumentChordVisualizer from "@/components/music/InstrumentChordVisualizer";
import { getChordNotes, parseChordSymbol } from "@/music/chords/chordParser";
import { getBassTriadDetails } from "@/music/chords/chordTheoryEngine";
import { getChordLabel } from "@/music/chords/chordSyncUtils";

const ChordLearningPanel = memo(
  ({
    chord,
    currentChord,
    activeNotes,
    nextChord,
    timeToNextChord,
    currentTime,
    chords,
    compact = false,
    showLabels = true,
    instrumentMode = "piano",
    onInstrumentModeChange,
    bassRootOnly = false,
    capo = 0,
    variant = "dark",
  }) => {
    const rawResolvedChord = currentChord || chord;
    const resolvedChord = rawResolvedChord ? getChordLabel(rawResolvedChord) : null;
    const resolvedNextChord = nextChord ? getChordLabel(nextChord) : null;
    const parsedChord = useMemo(() => parseChordSymbol(resolvedChord), [resolvedChord]);
    const bassTriad = useMemo(() => getBassTriadDetails(resolvedChord), [resolvedChord]);
    const safeInstrument = instrumentMode === "guitar" || instrumentMode === "bass" ? instrumentMode : "piano";
    const notes = useMemo(
      () => {
        if (Array.isArray(activeNotes) && activeNotes.length) return activeNotes;
        if (safeInstrument === "bass") return bassTriad.triad;
        return getChordNotes(resolvedChord);
      },
      [activeNotes, bassTriad.triad, resolvedChord, safeInstrument]
    );
    const hasChordText = Boolean(resolvedChord && resolvedChord !== "No chord");
    const hasRecognizedChord = Boolean(hasChordText && parsedChord.recognized);
    const displayChord = hasChordText ? resolvedChord : "Sin acorde detectado";
    const instrumentLabel = safeInstrument === "piano" ? "Piano" : safeInstrument === "guitar" ? "Guitar" : "Bass";
    const hasTimeline = Number.isFinite(currentTime) && Array.isArray(chords);

    return (
      <section
        className={`rounded-lg border p-3 ${variant === "light" ? "border-slate-200 bg-white" : "steel-studio-card border-cyan-300/15"}`}
        data-timeline-ready={hasTimeline ? "true" : "false"}
      >
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className={`text-xs uppercase tracking-[0.22em] ${variant === "light" ? "text-slate-400" : "text-cyan-100/60"}`}>Tutorial visual de acordes</p>
            <div className="mt-1 flex flex-wrap items-end gap-2">
              <h2 className={`font-black ${variant === "light" ? "text-slate-900" : "text-white"} ${compact ? "text-2xl sm:text-3xl" : "text-4xl md:text-5xl"}`}>
                {displayChord}
              </h2>
              <Badge className={variant === "light" ? "border border-teal-200 bg-teal-50 text-teal-700" : "border border-cyan-300/30 bg-cyan-300/10 text-cyan-100"}>
                {instrumentLabel}
              </Badge>
            </div>
            <p className={`mt-1 text-sm ${variant === "light" ? "text-slate-600" : "text-gray-300"}`}>
              {hasRecognizedChord
                ? safeInstrument === "bass"
                  ? bassRootOnly
                    ? `Raiz detectada: ${bassTriad.displayRoot}`
                    : `Triada de bajo: ${bassTriad.displayTriad.join(", ")}`
                  : `Notas iluminadas: ${notes.join(", ")}`
                : hasChordText
                  ? "Acorde no reconocido visualmente; mostramos el simbolo y dejamos el instrumento sin notas activas."
                  : "No hay notas activas para este instante. La cancion puede estar antes del primer acorde."}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {onInstrumentModeChange && (
              <div className={`grid grid-cols-3 gap-1 rounded-lg border p-1 ${variant === "light" ? "border-slate-200 bg-slate-50" : "border-white/10 bg-black/20"}`}>
                {["piano", "guitar", "bass"].map((instrument) => {
                  const active = safeInstrument === instrument;
                  return (
                    <button
                      key={instrument}
                      type="button"
                      aria-pressed={active}
                      onClick={() => onInstrumentModeChange(instrument)}
                      className={`min-h-9 rounded-xl px-3 text-sm font-semibold transition-colors ${
                        active
                          ? variant === "light" ? "bg-teal-600 text-white" : "bg-cyan-300 text-slate-950"
                          : variant === "light" ? "text-slate-600 hover:bg-slate-100" : "text-gray-300 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {instrument === "piano" ? "Piano" : instrument === "guitar" ? "Guitar" : "Bass"}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={`rounded-lg border px-3 py-1.5 text-sm ${variant === "light" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-white/10 bg-black/20 text-gray-300"}`}>
              <p className={`text-xs uppercase tracking-wide ${variant === "light" ? "text-slate-400" : "text-gray-500"}`}>Siguiente acorde</p>
              <p className={`mt-1 text-lg font-bold ${variant === "light" ? "text-teal-700" : "text-cyan-100"}`}>{resolvedNextChord || "Sin siguiente"}</p>
              <p className={`text-xs ${variant === "light" ? "text-slate-500" : "text-gray-400"}`}>
                {Number.isFinite(timeToNextChord) ? `Siguiente en ${timeToNextChord.toFixed(1)}s` : "Fin de la linea"}
              </p>
            </div>
          </div>
        </div>

        <InstrumentChordVisualizer
          instrumentMode={safeInstrument}
          chord={resolvedChord}
          activeNotes={notes}
          nextChord={resolvedNextChord}
          timeToNextChord={timeToNextChord}
          compact={compact}
          showLabels={showLabels}
          bassRootOnly={bassRootOnly}
          capo={capo}
          variant={variant}
        />
      </section>
    );
  }
);

ChordLearningPanel.displayName = "ChordLearningPanel";

export default ChordLearningPanel;
